/**
 * The signed-in user's account closure (SOL-201), as a hand-rolled external
 * store like `lib/auth.ts`: the banner, the locked screen and the Account page
 * all read one copy, with no provider in the tree.
 *
 * It fetches when the user becomes authenticated and on window focus (a closure
 * can be cancelled or reach its last day in another tab or by the clock), and
 * clears on sign-out. A failed fetch keeps what was known and says nothing: a
 * banner that cries "deleted" on a network blip is worse than none, and the
 * next focus retries.
 */
import { useCallback, useEffect, useSyncExternalStore } from 'react'
import type { AccountClosureView } from '../../../../types'
import { apiClient } from './api-client'
import { useAuth } from './auth'

type Closure = AccountClosureView['closure']

export interface AccountClosureState {
  closure: Closure
  /** True until the first answer for this session arrives. */
  loading: boolean
}

const EMPTY: AccountClosureState = { closure: null, loading: true }

let state: AccountClosureState = EMPTY
const listeners = new Set<() => void>()

/** Bumped on reset, so an answer for a session that ended is dropped. */
let generation = 0
let inFlight: Promise<void> | null = null

function setState(next: AccountClosureState): void {
  state = next
  listeners.forEach((l) => l())
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function getSnapshot(): AccountClosureState {
  return state
}

function reset(): void {
  generation += 1
  inFlight = null
  if (state !== EMPTY) setState(EMPTY)
}

function refresh(): Promise<void> {
  if (inFlight) return inFlight
  const mine = generation
  const pending = apiClient
    .getAccountClosure()
    .then((view) => {
      if (mine === generation) setState({ closure: view.closure, loading: false })
    })
    .catch(() => {
      // Show nothing new; the next focus retries.
      if (mine === generation && state.loading) setState({ closure: null, loading: false })
    })
    .finally(() => {
      if (inFlight === pending) inFlight = null
    })
  inFlight = pending
  return pending
}

/**
 * A failed request or cancel re-reads the server before rethrowing: a failure
 * does not say what happened (the mail failed and the undo did not confirm; a
 * 409 because the job already started; a 500 after the write landed), and the
 * banner and locked screen must show the closure that is really there.
 */
async function request(email: string): Promise<void> {
  const mine = generation
  let view: AccountClosureView
  try {
    view = await apiClient.requestAccountClosure(email)
  } catch (error) {
    void refresh()
    throw error
  }
  if (mine === generation) setState({ closure: view.closure, loading: false })
}

async function cancel(): Promise<void> {
  const mine = generation
  let view: AccountClosureView
  try {
    view = await apiClient.cancelAccountClosure()
  } catch (error) {
    void refresh()
    throw error
  }
  if (mine === generation) setState({ closure: view.closure, loading: false })
}

export interface UseAccountClosure extends AccountClosureState {
  refresh(): Promise<void>
  /** Throws the ApiError (EMAIL_MISMATCH, CLOSURE_MAIL_FAILED, CLOSURE_STATE_UNKNOWN, ...), after a refresh. */
  request(email: string): Promise<void>
  /** Throws the ApiError (CLOSURE_IN_PROGRESS, ...), after a refresh. */
  cancel(): Promise<void>
}

export function useAccountClosure(): UseAccountClosure {
  const { isAuthenticated } = useAuth()
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)

  useEffect(() => {
    // No request at all for a signed-out visitor.
    if (!isAuthenticated) {
      reset()
      return
    }
    if (state.loading) void refresh()
    const onFocus = (): void => void refresh()
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [isAuthenticated])

  const refreshCb = useCallback(() => refresh(), [])
  return { ...snapshot, refresh: refreshCb, request, cancel }
}

/** Test seam: forget everything between tests. */
export function resetAccountClosureForTests(): void {
  reset()
}
